import { Component, OnInit, ElementRef, ChangeDetectorRef, Renderer2 } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import moment from 'moment';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { PageEvent } from '@angular/material/paginator';
import { HttpParameter } from 'src/app/types/http-parameter';
import { PrintService } from 'src/app/services/print.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { FormBuilder } from '@angular/forms';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from '../../../services/currency.service';

@Component({
  selector: 'app-payments-past-due',
  templateUrl: './payments-past-due.component.html',
  styleUrls: ['./payments-past-due.component.css']
})
export class PaymentsPastDueComponent extends ViewTableComponent implements OnInit {

  currentPage: number;

  // owl date-range picker value ([start, end]).
  dateRange: Date[] = [];

  // Past-due rows are always dated before today, so block selecting future
  // dates in the range picker. A future range would be AND-ed with the
  // built-in "payment_date < today" filter and silently return zero rows.
  // Getter: "today" is the STUDIO's current date (field_studio_timezone,
  // loaded async, same source as day-view), at end-of-day so the studio's
  // today stays selectable as a range end.
  get today(): Date {
    return this.reportDateService.studioTodayEnd();
  }

  override displayedColumns: string[] = [
    'oldest_due_date',
    'student_name',
    'enrollment_title',
    'amount_due',
    'total_balance',
    'overdue_count',
    'lessons_remaining',
    'instructor',
  ];

  constructor(
    private _printService: PrintService,
    public override _refreshService: RefreshService,
    public override _dialogService: DialogService,
    public override _entityRESTService: EntityRESTService,
    public override _utilityService: UtilityService,
    public override _fieldsService: FieldsService,
    public override _drupalRESTService: DrupalRESTService,
    public override _taxonomyService: TaxonomyService,
    public override _router: Router,
    public override _activatedRoute: ActivatedRoute,
    public override _authRESTService: AuthRESTService,
    public override _authService: AuthService,
    public override _menuRESTService: MenuRESTService,
    public override _formBuilder: FormBuilder,
    public override _routeEventsService: RouteEventsService,
    public override _permissionsService: PermissionsService,
    public override _renderer2: Renderer2,
    public override _configPages: ConfigPagesService,
    public override _packagesService: PackagesService,
    public override _snackBar: MatSnackBar,
    public override _userRESTService: UserService,
    public override _el: ElementRef,
    public override _changeDetectorRef: ChangeDetectorRef,
    public override _sanitizer: DomSanitizer,
    public override _studioConfigService: StudioConfigService,
    public override _componentRefreshService: ComponentRefreshService,
    public override _translateService: TranslateService,
    private currencyService: CurrencyService,
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService
    );
  }

  // Push the owl-picked range through getData as start_date/end_date, which the
  // existing getData() converts into the field_payment_date contextual param.
  dateRangeChange(event: any): void {
    if (!event?.value || !Array.isArray(event.value)) {
      return;
    }
    this.dateRange = event.value;
    const params: HttpParameter[] = [];
    if (this.dateRange[0] && this.dateRange[1]) {
      params.push(
        { parameter: 'start_date', value: moment(this.dateRange[0]).format('YYYY-MM-DD') },
        { parameter: 'end_date', value: moment(this.dateRange[1]).format('YYYY-MM-DD') },
      );
    }
    this.getData(params);
  }

  private formatCurrency(value: number | string | null | undefined): string {
    return this._utilityService.formatCurrency(value);
  }

  private formatDate(dateString: string | null | undefined): string {
    return this.reportDateService.formatDate(dateString, 'N/A');
  }

  override ngOnInit() {
    // Initialize pagination values
    this.paginationIndex = 0;
    this.pageSize = 10;  // Set default page size

    // Call parent ngOnInit if it exists
    super.ngOnInit();

    // Initial data load with pagination parameters
    this.getData([
      { parameter: 'page', value: this.paginationIndex.toString() },
      { parameter: 'items_per_page', value: this.pageSize.toString() }
    ]);
  }

  // ami_payments_due_report: one row per enrollment (real balance + oldest
  // overdue date), not one row per scheduled-payment installment. The studio
  // is auto-injected by DrupalRESTService; start_date/end_date (if set) bound
  // the overdue window and are consumed directly by the endpoint.
  private static readonly ENDPOINT = '/api/payments-due-report/past-due';

  /**
   * Fill in the pagination and sorting the backend expects, leaving any
   * caller-supplied page/items_per_page alone. Shared by getData() and
   * onPrint() so the printed report reflects the same filters and ordering as
   * the on-screen table.
   */
  private buildRequestParams(params: { parameter: string; value: string; }[]) {
    let finalParams = [...params];

    // Add current pagination state to params if not present
    if (!params.some(p => p.parameter === 'page')) {
      finalParams.push(
        { parameter: 'page', value: this.paginationIndex.toString() },
        { parameter: 'items_per_page', value: this.pageSize.toString() }
      );
    }

    // Add sorting parameters if available
    if (this.sortParams !== undefined) {
      finalParams.push(this.sortParams);
    }

    return finalParams;
  }

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;

    // Store only non-pagination params in queryParams
    this.queryParams = params.filter(param =>
      param.parameter !== 'page' && param.parameter !== 'items_per_page'
    );

    const finalParams = this.buildRequestParams(params);

    this._drupalRESTService.httpGET(PaymentsPastDueComponent.ENDPOINT, finalParams)
      .subscribe((data) => {
        this.ShowProgressBar = false;
        this.dataSource = new MatTableDataSource(data['results']);
        if (data['results']) {
          this.setupPagination(data, params);
        }
      },
        error => this.handleError(error)
      )
  }

  /**
   * Custom print method for the past-due report.
   *
   * The on-screen table is server-paged, so printing from dataSource.data would
   * only ever emit the current page (10 rows by default) while looking like the
   * whole report. Re-fetch with a single large page instead, so the printout
   * covers every row the filters matched.
   *
   * Deliberately NOT items_per_page=0: this endpoint applies range()
   * unconditionally and clamps the value with max(1, min($items, 200)), so 0
   * would clamp to 1 row, the opposite of "all rows". 200 mirrors that server
   * clamp: it is the most this endpoint can ever return, so asking for more
   * would silently yield 200 anyway and misreport what got printed.
   */
  private static readonly PRINT_ROW_LIMIT = 200;

  override onPrint() {
    // Must happen here, in the click handler: the fetch below ends the user
    // gesture, so opening the window from its callback trips the popup blocker.
    const printWindow = this._printService.openPrintWindow();
    if (!printWindow) {
      this._snackBar.open(
        'Your browser blocked the print window. Allow pop-ups for this site and try again.',
        'Close', { duration: 8000 });
      return;
    }

    this.ShowProgressBar = true;

    const limit = PaymentsPastDueComponent.PRINT_ROW_LIMIT;
    const params = this.buildRequestParams([
      ...this.queryParams,
      { parameter: 'page', value: '0' },
      { parameter: 'items_per_page', value: limit.toString() },
    ]);

    this._drupalRESTService.httpGET(PaymentsPastDueComponent.ENDPOINT, params)
      .subscribe((data) => {
        this.ShowProgressBar = false;

        // A dead PHP worker surfaces as a 200 with an empty body, which would
        // otherwise be indistinguishable from a genuinely empty result set.
        if (!data || data['results'] === undefined) {
          printWindow.close();
          this._snackBar.open(
            'Could not build the printout. The report did not return. Try a narrower date range.',
            'Close', { duration: 8000 });
          return;
        }

        const rows = data['results'] || [];
        if (!rows.length) {
          printWindow.close();
          this._snackBar.open('No past due payments found for the selected criteria.', 'Close', { duration: 5000 });
          return;
        }

        // The 200-row server clamp is reachable, so say so rather than
        // truncating silently.
        const total = Number(data['count'] ?? rows.length);
        if (total > rows.length) {
          this._snackBar.open(
            `This range matches ${total} past due enrollments; printing the first ${rows.length}. Narrow the date range to print the rest.`,
            'Close', { duration: 8000 });
        }

        this.renderPrintout(rows, printWindow);
      },
        error => {
          this.ShowProgressBar = false;
          printWindow.close();
          this.handleError(error);
        }
      );
  }

  /**
   * Build and hand off the printable HTML for an already-fetched result set.
   */
  private renderPrintout(rows: any[], printWindow: Window | null) {
    let content = `
      <div class="report-container">
        <h1 class="report-title">Payments Past Due Report</h1>

        <div class="report-section">
          <table class="report-table">
            <thead>
              <tr>
                <th>Oldest Due Date</th>
                <th>Student</th>
                <th>Enrollment</th>
                <th>Amount Due</th>
                <th>Total Balance</th>
                <th>Overdue Payments</th>
                <th>Lessons Remaining</th>
                <th>Teacher</th>
              </tr>
            </thead>
            <tbody>`;

    // Add rows from the fetched data. Callers guarantee a non-empty set.
    rows.forEach((row: any) => {
      content += `
          <tr>
            <td>${this.formatDate(row.oldest_due_date)}</td>
            <td>${this.escapeHtml(row.student_name) || 'N/A'}</td>
            <td>${this.escapeHtml(row.enrollment_title) || 'N/A'}</td>
            <td>${this.currencyService.format(row.amount_due)}</td>
            <td>${this.currencyService.format(row.total_balance)}</td>
            <td>${row.overdue_count ?? 0}</td>
            <td>${row.lessons_remaining || '0'}</td>
            <td>${this.escapeHtml(row.instructor)}</td>
          </tr>`;
    });

    content += `
            </tbody>
          </table>
        </div>
      </div>`;

    const styles = `
      .report-container {
        font-family: Arial, sans-serif;
        margin: 0 auto;
        padding: 20px;
        max-width: 100%;
      }
      .report-title {
        font-size: 24px;
        text-align: center;
        margin-bottom: 20px;
        color: #333;
      }
      .report-section {
        margin-bottom: 30px;
        page-break-inside: avoid;
      }
      .report-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }
      .report-table th, .report-table td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      }
      .report-table th {
        background-color: #f2f2f2;
        font-weight: bold;
      }
      .report-table tr:nth-child(even) {
        background-color: #f9f9f9;
      }
      .no-data {
        text-align: center;
        font-style: italic;
        color: #999;
      }
      @media print {
        .report-section {
          page-break-inside: avoid;
        }
      }
    `;

    this._printService.print(content, {
      title: 'Payments Past Due Report',
      styles: styles,
      showSchoolName: true,
      promptPrint: true
    }, printWindow);
  }

  override resetForm() {
    // Clear all stored params
    this.queryParams = [];
    this.sortParams = undefined;

    // Reset pagination
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginationIndex = 0;  // Reset the pagination index
      this.pageSize = 10;  // Reset page size
    }

    // Clear the form
    this.form.resetForm();

    // Clear the date range picker
    this.dateRange = [];

    // Clear any autocomplete options
    this.autoCompleteEnrollmentOptions = [];

    // Make a fresh request with explicitly empty params
    this.getData([
      { parameter: 'page', value: '0' },
      { parameter: 'items_per_page', value: '10' }
    ]);

    // Update router last
    this.updateRouterParams({});
  }

  override pageChanged(event: PageEvent) {
    // Update pagination state
    this.paginationIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    // Always include pagination parameters
    const paginationParams: HttpParameter[] = [
      { parameter: 'page', value: this.paginationIndex.toString() },
      { parameter: 'items_per_page', value: this.pageSize.toString() }
    ];

    // Filter out any existing pagination params from queryParams
    const filteredQueryParams = this.queryParams.filter(param =>
      param.parameter !== 'page' && param.parameter !== 'items_per_page'
    );

    // Combine filtered query params with new pagination params
    const combinedParams = [...filteredQueryParams, ...paginationParams];
    this.getData(combinedParams);
  }

  override onPageSizeOptions() {
    return [10, 25, 50];
  }

  override setupPagination(data: any, params: { parameter: string; value: string; }[]) {
    const pageParam = params.find(param => param.parameter === 'page');
    if (this.paginator) {
      this.paginator.length = data?.['count'] ?? 0;
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageIndex = pageParam ? parseInt(pageParam.value, 10) : 0;
    }
    // DO NOT call updateRouterParams here - prevents duplicate requests
  }

  private escapeHtml(value: string | null | undefined): string {
    if (value === null || value === undefined) { return ''; }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
